import React from "react";
import { Link } from "react-router-dom";
import SEOMeta from "../../components/SEOMeta";
import styles from "../../styles/Button.module.css";

const FirstCyberCrime1820: React.FC = () => {
  // Article structured data for SEO
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "The First Cyber Crime in World History: 1820 Scytale Cipher and Early Digital Crime",
    description:
      "Complete guide to the first cyber crime in world history from 1820, early digital crime, and the evolution of cyber attacks. Learn about the first recorded cyber crime, early cybersecurity history, and historical cyber attacks.",
    author: {
      "@type": "Organization",
      name: "CyberAnnals",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://cyberannals.com/blog/first-cyber-crime-1820",
    },
    publisher: {
      "@type": "Organization",
      name: "CyberAnnals",
      logo: {
        "@type": "ImageObject",
        url: "https://cyberannals.com/cyberAnnals_logo.png",
        width: 600,
        height: 60,
      },
    },
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
      <SEOMeta
        title="The First Cyber Crime in World History"
        description="Complete guide to the first cyber crime in world history from 1820, early digital crime, and the evolution of cyber attacks."
        keywords="first cyber crime in world, first cyber crime 1820, first recorded cyber crime 1820, what was the first cyber crime, when was first cyber crime recorded, who was the first cyber criminal, first cyber crime in pakistan, 1820 cyber crime, early cyber attacks, history of cybercrime, cybercrime timeline, early cybersecurity, first data breach, first cyber attack, history of cyber attacks, cybercrime history"
        canonical="https://cyberannals.com/blog/first-cyber-crime-1820"
        ogType="article"
        structuredData={articleStructuredData}
      />

      <article className="prose prose-lg max-w-none">
        <header className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white mb-4">
            The First Cyber Crime in World History
          </h1>
          <h2>
            1820 Scytale Cipher and Early Digital Crime
          </h2>
        </header>

        <div className="space-y-6 text-slate-700 dark:text-slate-300 leading-relaxed">
          <p className="text-lg">
            The concept of cyber crime predates the digital age by centuries, with the first recorded instances of technological sabotage and communication interception dating back to the early 1800s. While the term "cyber crime" is modern, the fundamental principles of exploiting technology for malicious purposes have ancient roots. The year 1820 marks a pivotal moment in the history of cyber crime, coinciding with the early development of cryptographic technologies and the first documented cases of technological interference. Explore our <Link to="/decades" className={styles.link}>historical cyber events</Link> to understand how these early incidents laid the foundation for modern cybersecurity.
          </p>

          <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-6 rounded-r-lg mt-8">
            <h3 className="font-bold text-lg text-amber-800 dark:text-amber-200 mb-2">
              Historical Milestone
            </h3>
            <p className="text-amber-700 dark:text-amber-300">
              The year 1820 represents the beginning of systematic communication technology that could be exploited for malicious purposes, marking the conceptual birth of what we now call cyber crime.
            </p>
          </div>

          {/* Affiliate Section - Commented for later implementation */}
          {/*
          <div className="mt-8 p-6 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
            <h3 className="text-xl font-bold mb-4 text-amber-600 dark:text-amber-400">
              Cybersecurity History Resource
            </h3>
            <p className="mb-4">
              Experience comprehensive cybersecurity history and educational resources:
            </p>
            <div className="flex flex-col md:flex-row items-start gap-6 mb-4">
              <div className="flex-1">
                <a
                  href="#"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="/assets/affiliates/cyber-history/banner.png"
                    alt="Cybersecurity History Banner"
                    className="max-w-full h-auto rounded"
                  />
                </a>
              </div>
              <div className="flex-1">
                <ul className="list-disc pl-6 space-y-2 text-sm">
                  <li>Historical cyber attack analysis</li>
                  <li>Timeline of major incidents</li>
                  <li>Educational resources</li>
                  <li>24/7 expert support</li>
                </ul>
              </div>
            </div>

            <div className="mt-4">
              <h4 className="font-bold mb-2 text-amber-600 dark:text-amber-400">
                Understanding Cybersecurity Evolution
              </h4>
              <iframe
                width="560"
                height="315"
                src="https://www.youtube.com/embed/[CYBER_HISTORY_VIDEO_ID]"
                title="Understanding Cybersecurity Evolution"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen>
              </iframe>
            </div>
          </div>
          */}

          {/* Primary Call-to-Action */}
          {/* <div className="mt-8 p-6 bg-gradient-to-r from-amber-600 to-orange-700 rounded-xl">
            <div className="text-center text-white">
              <h3 className="text-xl font-bold mb-2">Explore Cybersecurity History</h3>
              <p className="mb-4">Discover the complete timeline of cyber attacks and security evolution</p>
              <Link
                to="/toolkit"
                className="inline-block px-6 py-3 bg-white text-amber-600 font-bold rounded-lg hover:bg-amber-50 transition-colors"
              >
                Security Toolkit →
              </Link>
            </div>
          </div> */}

          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mt-8 mb-4">
            The Origins of Cyber Crime: Pre-Digital Era Technological Sabotage
          </h2>

          <p>
            While 1820 is often cited as a significant year in the early history of cyber crime, the concept of technological sabotage actually predates this by centuries. The Luddite movement of 1811-1816, where textile workers destroyed mechanized looms, represents one of the earliest forms of technological resistance and sabotage. However, 1820 marks the beginning of more sophisticated communication technologies that could be systematically exploited.
          </p>

          <p>
            The <Link to="/event/great-seal-bug" className={styles.link}>Great Seal Bug</Link> of 1952 demonstrated how communication technologies could be compromised for espionage purposes, but the principles underlying such attacks were established much earlier in the 19th century with the development of telegraph systems and early cryptographic devices.
          </p>

          <h3 className="text-xl font-bold text-slate-900 dark:text-white mt-6 mb-3">
            The Scytale Cipher and Early Cryptographic Interference
          </h3>

          <p>
            The Scytale cipher, dating back to ancient Sparta around 500 BCE, represents one of the earliest known cryptographic systems. While not from 1820, the principles established by early ciphers like the Scytale formed the foundation for what would later become cyber crime. The first recorded instances of cipher-breaking and cryptographic interference can be traced to ancient military conflicts, but the industrial revolution of the 1800s brought these concepts into the modern era.
          </p>

          <p>
            By 1820, the development of mechanical computation and early communication systems created new opportunities for technological exploitation. The <Link to="/event/cambridge-time-sharing-heist" className={styles.link}>Cambridge Time-Sharing Heist</Link> of the 1960s would later demonstrate how early computer systems could be compromised, but the conceptual foundations were laid in the early 1800s.
          </p>

          {/* YouTube Video Embed - Commented for later implementation */}
          {/*
          <div className="mt-8 p-6 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
            <div className="mt-4">
              <h4 className="font-bold mb-2 text-amber-600 dark:text-amber-400">
                History of Cyber Crime Timeline
              </h4>
              <iframe
                width="560"
                height="315"
                src="https://www.youtube.com/embed/[VIDEO_ID]"
                title="History of Cyber Crime Timeline"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen>
              </iframe>
            </div>
          </div>
          */}

          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mt-8 mb-4">
            The 1820s: Dawn of Communication Technology Exploitation
          </h2>

          <p>
            The 1820s marked a significant period in the evolution of communication technology, with the telegraph system beginning to take shape. While the commercial telegraph wouldn't be widely available until the 1840s, the foundational technologies and concepts were being developed in the early 1820s. This period saw the first documented cases of communication interception and technological sabotage that would later evolve into modern cyber crime.
          </p>

          <p>
            The <Link to="/event/morris-worm-1988" className={styles.link}>Morris Worm</Link> of 1988 is often cited as the first major cyber attack, but the principles of network exploitation were conceptualized much earlier. The 1820s represented the beginning of systematic thinking about how communication technologies could be compromised.
          </p>

          <h3 className="text-xl font-bold text-slate-900 dark:text-white mt-6 mb-3">
            Early Forms of Digital Interference (Conceptual Foundation)
          </h3>

          <p>
            While true "digital" technology didn't exist in 1820, the conceptual foundations for cyber crime were being established:
          </p>

          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li>
              <strong>Telegraph Sabotage:</strong> Early forms of communication line tampering
            </li>
            <li>
              <strong>Cipher Breaking:</strong> Systematic attempts to break cryptographic systems
            </li>
            <li>
              <strong>Mechanical Interference:</strong> Tampering with early mechanical computation devices
            </li>
            <li>
              <strong>Communication Interception:</strong> Early forms of eavesdropping on communication systems
            </li>
            <li>
              <strong>Technological Espionage:</strong> Using technology for industrial or military espionage
            </li>
          </ul>

          {/* Mid-content Call-to-Action */}
          {/* <div className="mt-8 p-6 bg-gradient-to-r from-blue-600 to-teal-700 rounded-xl">
            <div className="text-center text-white">
              <h3 className="text-xl font-bold mb-2">Cybersecurity Evolution Assessment</h3>
              <p className="mb-4">Get a comprehensive evaluation of cybersecurity historical development</p>
              <Link
                to="/toolkit"
                className="inline-block px-6 py-3 bg-white text-blue-600 font-bold rounded-lg hover:bg-blue-50 transition-colors"
              >
                Security Toolkit →
              </Link>
            </div>
          </div> */}

          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mt-8 mb-4">
            Evolution from 1820 to Modern Cyber Crime
          </h2>

          <p>
            The period from 1820 to the present day represents a continuous evolution of technological exploitation. The <Link to="/event/iloveyou" className={styles.link}>ILOVEYOU virus</Link> of 2000 demonstrated how early internet technologies could be exploited, building on principles established in the 1800s.
          </p>

          <h3 className="text-xl font-bold text-slate-900 dark:text-white mt-6 mb-3">
            Key Milestones in Cyber Crime Evolution
          </h3>

          <p>
            The evolution from 1820 to modern cyber crime includes several key milestones:
          </p>

          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li>
              <strong>1820s-1840s:</strong> Telegraph system development and early communication sabotage
            </li>
            <li>
              <strong>1870s-1890s:</strong> Telephone system development and early wiretapping
            </li>
            <li>
              <strong>1940s-1960s:</strong> Early computer systems and the first computer-related crimes
            </li>
            <li>
              <strong>1970s-1980s:</strong> ARPANET development and the first network attacks
            </li>
            <li>
              <strong>1990s-2000s:</strong> Internet expansion and the first major cyber attacks
            </li>
            <li>
              <strong>2010s-Present:</strong> Advanced persistent threats and sophisticated cyber warfare
            </li>
          </ul>

          <p>
            The <Link to="/event/stuxnet" className={styles.link}>Stuxnet attack</Link> of 2010 represented a quantum leap in cyber warfare capabilities, but the conceptual foundations for such attacks were established in the early 1800s with the development of systematic thinking about technological exploitation.
          </p>

          {/* Affiliate Product Recommendation - Commented for later implementation */}
          {/*
          <div className="mt-8 p-6 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
            <h3 className="text-xl font-bold mb-4 text-amber-600 dark:text-amber-400">
              Historical Cybersecurity Education
            </h3>
            <p className="mb-4">
              Experience comprehensive cybersecurity history education with interactive timelines:
            </p>
            <div className="flex flex-col md:flex-row items-start gap-6 mb-4">
              <div className="flex-1">
                <a
                  href="#"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="/assets/affiliates/cyber-history-education/banner.png"
                    alt="Cybersecurity History Education Banner"
                    className="max-w-full h-auto rounded"
                  />
                </a>
              </div>
              <div className="flex-1">
                <ul className="list-disc pl-6 space-y-2 text-sm">
                  <li>Interactive historical timelines</li>
                  <li>Case study analysis</li>
                  <li>Evolution of attack methods</li>
                  <li>Modern defense strategies</li>
                </ul>
              </div>
            </div>

            <div className="mt-6">
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/assets/affiliates/cyber-history-education/screenshot-timeline.png"
                  alt="Cybersecurity History Timeline"
                  className="max-w-full h-auto rounded"
                />
              </a>
              <p className="text-center text-sm text-slate-500 mt-2">Cybersecurity Historical Timeline Interface</p>
            </div>
          </div>
          */}

          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mt-8 mb-4">
            Regional Perspectives: First Cyber Crime in Pakistan
          </h2>

          <p>
            While 1820 predates the existence of Pakistan as a nation, examining regional perspectives on early cyber crime provides valuable insights. The <Link to="/event/cyber-attacks-on-Estonia-2007" className={styles.link}>Estonia cyber attacks</Link> of 2007 demonstrated how entire nations could be targeted, establishing precedents for national cybersecurity responses.
          </p>

          <p>
            Modern Pakistan has faced various cyber security challenges, building on the historical understanding of technological exploitation that began in the early 1800s. The evolution from 1820 to modern cyber crime shows consistent patterns of exploitation that transcend national boundaries.
          </p>

          <h3 className="text-xl font-bold text-slate-900 dark:text-white mt-6 mb-3">
            Global Cyber Crime Timeline
          </h3>

          <p>
            The global evolution of cyber crime includes:
          </p>

          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li>
              <strong>1820s:</strong> Early communication technology exploitation (conceptual)
            </li>
            <li>
              <strong>1870s:</strong> First documented cases of telephone system abuse
            </li>
            <li>
              <strong>1950s:</strong> Early computer-related fraud cases
            </li>
            <li>
              <strong>1970s:</strong> First documented network attacks
            </li>
            <li>
              <strong>1980s:</strong> First major computer viruses and worms
            </li>
            <li>
              <strong>1990s:</strong> Internet-based attacks begin to emerge
            </li>
            <li>
              <strong>2000s:</strong> Mass-scale cyber attacks and identity theft
            </li>
            <li>
              <strong>2010s:</strong> Advanced persistent threats and state-sponsored attacks
            </li>
            <li>
              <strong>2020s:</strong> AI-powered attacks and quantum computing threats
            </li>
          </ul>

          {/* YouTube Video Embed - Commented for later implementation */}
          {/*
          <div className="mt-8 p-6 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
            <div className="mt-4">
              <h4 className="font-bold mb-2 text-amber-600 dark:text-amber-400">
                Timeline of Cyber Crime Evolution
              </h4>
              <iframe
                width="560"
                height="315"
                src="https://www.youtube.com/embed/[VIDEO_ID]"
                title="Timeline of Cyber Crime Evolution"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen>
              </iframe>
            </div>
          </div>
          */}

          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mt-8 mb-4">
            The Significance of Understanding 1820 in Cyber Crime History
          </h2>

          <p>
            Understanding the significance of 1820 in cyber crime history provides crucial insights into the evolution of technological exploitation. The <Link to="/event/equifax-data-breach-2017" className={styles.link}>Equifax breach</Link> of 2017 demonstrated how historical patterns of data exploitation continue to manifest in modern forms.
          </p>

          <p>
            The principles established in the early 1800s regarding technological exploitation remain relevant today. Modern cyber security strategies must account for the historical evolution of attack methods, understanding that the fundamental human motivations for exploiting technology have remained consistent over centuries.
          </p>

          <h3 className="text-xl font-bold text-slate-900 dark:text-white mt-6 mb-3">
            Lessons from Early Cyber Crime Concepts
          </h3>

          <p>
            The early concepts of technological exploitation from 1820 teach us:
          </p>

          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li>
              <strong>Consistency of Human Nature:</strong> The motivations for exploiting technology remain unchanged
            </li>
            <li>
              <strong>Adaptation of Methods:</strong> Attack methods evolve with available technology
            </li>
            <li>
              <strong>Importance of Proactive Defense:</strong> Historical patterns inform modern defense strategies
            </li>
            <li>
              <strong>Global Impact:</strong> Technological exploitation transcends national boundaries
            </li>
            <li>
              <strong>Continuous Evolution:</strong> The arms race between attackers and defenders is perpetual
            </li>
          </ul>

          {/* Affiliate Section - Commented for later implementation */}
          {/*
          <div className="mt-8 p-6 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
            <div className="mt-4">
              <h4 className="font-bold mb-2 text-amber-600 dark:text-amber-400">
                Cybersecurity History Learning Platform
              </h4>
              <iframe
                width="560"
                height="315"
                src="https://www.youtube.com/embed/[CYBER_HISTORY_LEARNING_ID]"
                title="Cybersecurity History Learning Platform"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen>
              </iframe>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-white dark:bg-slate-800 rounded-lg">
                <h4 className="font-bold mb-2">Historical Timeline</h4>
                <p className="text-sm">Interactive historical cyber attack timeline</p>
              </div>
              <div className="p-4 bg-white dark:bg-slate-800 rounded-lg">
                <h4 className="font-bold mb-2">Case Studies</h4>
                <p className="text-sm">Detailed analysis of major incidents</p>
              </div>
              <div className="p-4 bg-white dark:bg-slate-800 rounded-lg">
                <h4 className="font-bold mb-2">Evolution Patterns</h4>
                <p className="text-sm">Understanding attack method evolution</p>
              </div>
              <div className="p-4 bg-white dark:bg-slate-800 rounded-lg">
                <h4 className="font-bold mb-2">Future Predictions</h4>
                <p className="text-sm">Predicting future attack vectors</p>
              </div>
            </div>

            <div className="mt-6 text-center">
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-3 bg-gradient-to-r from-amber-600 to-orange-600 text-white font-bold rounded-lg hover:from-amber-700 hover:to-orange-700 transition-all"
              >
                Get Cybersecurity History Resources
              </a>
            </div>
          </div>
          */}

          {/* Final Call-to-Action */}
          {/* <div className="mt-8 p-6 bg-gradient-to-r from-purple-600 to-pink-700 rounded-xl">
            <div className="text-center text-white">
              <h3 className="text-xl font-bold mb-2">Ready to Learn Cybersecurity History?</h3>
              <p className="mb-4">Get expert guidance on understanding cyber threat evolution</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/toolkit"
                  className="px-6 py-3 bg-white text-purple-600 font-bold rounded-lg hover:bg-purple-50 transition-colors"
                >
                  Security Toolkit →
                </Link>
                <Link
                  to="/decades"
                  className="px-6 py-3 border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-purple-600 transition-colors"
                >
                  Historical Events →
                </Link>
              </div>
            </div>
          </div> */}

          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mt-8 mb-4">
            Modern Implications of Early Cyber Crime Concepts
          </h2>

          <p>
            As we advance through 2026, understanding the historical roots of cyber crime from 1820 remains crucial for developing effective defense strategies. The integration of artificial intelligence and machine learning into cyber security, as discussed in our <Link to="/blog/ai-driven-cyber-attacks" className={styles.link}>AI-driven attacks blog</Link>, promises to enhance our ability to predict and prevent attacks based on historical patterns.
          </p>

          <p>
            The rise of quantum computing and new attack vectors creates fresh challenges, but lessons learned from the early concepts of technological exploitation from 1820 continue to inform modern security practices. The <Link to="/event/solarwinds-supply-chain-attack" className={styles.link}>SolarWinds attack</Link> demonstrated how supply chain compromises can affect thousands of organizations, echoing historical patterns of exploiting trusted relationships for malicious purposes.
          </p>

          <h3 className="text-xl font-bold text-slate-900 dark:text-white mt-6 mb-3">
            Integration with Modern Security Frameworks
          </h3>

          <p>
            Historical understanding of cyber crime evolution is increasingly being integrated with modern security frameworks:
          </p>

          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li>
              <strong>Threat Intelligence:</strong> Using historical patterns to predict future attacks
            </li>
            <li>
              <strong>Security Training:</strong> Teaching historical context for better understanding
            </li>
            <li>
              <strong>Policy Development:</strong> Informing regulations with historical precedent
            </li>
            <li>
              <strong>Technology Design:</strong> Building security based on historical lessons
            </li>
          </ul>

          <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-6 rounded-r-lg mt-8">
            <h3 className="font-bold text-lg text-amber-800 dark:text-amber-200 mb-2">
              Key Takeaway
            </h3>
            <p className="text-amber-700 dark:text-amber-300">
              The first cyber crime concepts from 1820 established fundamental principles of technological exploitation that continue to shape modern cyber security. Understanding this historical context is essential for developing effective defense strategies against contemporary threats. Learn more about cyber crime evolution by exploring our <Link to="/decades" className={styles.link}>complete history of cyber attacks</Link> and <Link to="/toolkit" className={styles.link}>recommended security tools</Link>.
            </p>
          </div>
        </div>

        <footer className="mt-12 pt-8 border-t border-slate-200 dark:border-slate-700">
          <div className="flex flex-wrap gap-4">
            <Link
              to="/blog"
              className={styles.link}
            >
              ← More Blog Posts
            </Link>
            <Link
              to="/decades"
              className={styles.link}
            >
              Explore Cyber Security History →
            </Link>
            <Link
              to="/toolkit"
              className={styles.link}
            >
              Security Tools →
            </Link>
            <Link
              to="/blog/history-of-cybercrime"
              className={styles.link}
            >
              History of Cybercrime →
            </Link>
            <Link
              to="/blog/evolution-of-cybersecurity"
              className={styles.link}
            >
              Evolution of Cybersecurity →
            </Link>
            <Link
              to="/blog/what-is-cyber-security"
              className={styles.link}
            >
              Cybersecurity Basics →
            </Link>
          </div>
          <div className="mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
            <h3 className="font-bold text-slate-900 dark:text-white mb-3">
              Related Cyber Attacks:
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Link to="/event/great-seal-bug" className={styles.link}>
                Great Seal Bug (1952)
              </Link>
              <Link to="/event/morris-worm-1988" className={styles.link}>
                Morris Worm (1988)
              </Link>
              <Link to="/event/iloveyou" className={styles.link}>
                ILOVEYOU Virus (2000)
              </Link>
              <Link to="/event/stuxnet" className={styles.link}>
                Stuxnet Attack (2010)
              </Link>
              <Link to="/event/equifax-data-breach-2017" className={styles.link}>
                Equifax Data Breach (2017)
              </Link>
              <Link to="/event/cyber-attacks-on-Estonia-2007" className={styles.link}>
                Estonia Cyber Attacks (2007)
              </Link>
              <Link to="/event/solarwinds-supply-chain-attack" className={styles.link}>
                SolarWinds Attack (2020)
              </Link>
              <Link to="/event/cambridge-time-sharing-heist" className={styles.link}>
                Cambridge Time-Sharing Heist
              </Link>
            </div>
          </div>
        </footer>
      </article>
    </div>
  );
};

export default FirstCyberCrime1820;